import React, { useState } from 'react';
import { MdUnfoldLess, MdUnfoldMore } from 'react-icons/md';
import {
  Block, ColumnCard, DECK_GRID, DECK_GRID_PLAIN, FlowConnector, Footnote, GovernedBy,
  ShareholderBar, SlideHead, StepRail,
} from './DeckUI';

// One slide of the plan: a Shareholder bar where the slide has one, three books or three
// capabilities, the flows between them, and a build sequence where the slide has one.
//
// Both structural slides render through here rather than each owning a copy, so the two
// cannot drift into looking like different documents.
//
// EVERYTHING STARTS CLOSED. The reader lands on the shape of the thing — three headers and
// the arrows between them, one screen even on a phone — and opens the part they want. The
// previous version showed one book in full immediately, which answered a question nobody
// had asked yet and buried the other two below the fold. "Show everything" restores the
// full read in one press, for anyone who wants the slide rather than the summary.

export default function SlideDeck({ slide }) {
  const [open, setOpen] = useState({});

  const allOpen = slide.columns.every((c) => open[c.key]);
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const toggleAll = () => setOpen(
    allOpen ? {} : Object.fromEntries(slide.columns.map((c) => [c.key, true])),
  );

  const distributions = (slide.flows || []).find((f) => f.to === 'top');
  // Flows BETWEEN columns, which is what decides the grid. The distributions flow goes to
  // the Shareholder bar, not to a column, so it does not earn a connector track.
  const hasConnectors = (slide.flows || []).some((f) => f.to !== 'top');
  // The column the Shareholder flow leaves from — the hub every other column feeds. Derived
  // from the flows so nothing here has to know the word "liquidity".
  const hub = slide.columns.find((c) => c.key === distributions?.from) || null;
  const feeders = hub ? slide.columns.filter((c) => c.key !== hub.key) : [];

  return (
    <div>
      <SlideHead headline={slide.headline} />

      {/* ABOVE the Shareholder bar, deliberately. The distribution flow is drawn as one
          unbroken line from the Liquidity Pool up into the bar; anything sitting between
          the two would cut it in half. Do not move this back down. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="min-w-0 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
          Select a heading to open it.
        </p>
        <button
          type="button"
          onClick={toggleAll}
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:border-brand-gold hover:text-brand-gold dark:border-gray-700 dark:text-gray-300"
        >
          {allOpen ? <MdUnfoldLess size={14} /> : <MdUnfoldMore size={14} />}
          {allOpen ? 'Collapse all' : 'Show everything'}
        </button>
      </div>

      {slide.top && (
        <ShareholderBar
          label={slide.top}
          flowLabel={distributions?.label}
          /* Which column the distribution actually leaves, so the stacked layout can name
             it rather than draw a connector to whichever card happens to be on top. */
          flowFrom={slide.columns.find((c) => c.key === distributions?.from)?.short}
        />
      )}

      {/* STACKED: the hub goes on top, directly under the Shareholder bar, with the columns
          that feed it side by side beneath. The deck's left-to-right order (Core, Liquidity,
          Run-off) is a landscape reading order; stacked verbatim it put the Core book under
          the bar and made the distribution look like it came from there.

          The hub is DERIVED, not named: it is the column the Shareholder flow leaves from.
          Nothing here hardcodes "liquidity". */}
      {hub && (
        <div className="lg:hidden">
          <ColumnCard column={hub} open={!!open[hub.key]} onToggle={() => toggle(hub.key)}>
            {hub.blocks.map((b) => <Block key={b.title} tone={hub.tone} block={b} />)}
            <GovernedBy tone={hub.tone} items={hub.governedBy} />
          </ColumnCard>

          <div className="grid grid-cols-2">
            {feeders.map((c) => (
              <FlowConnector key={c.key} flows={slide.flows || []}
                             fromKey={hub.key} toKey={c.key} />
            ))}
          </div>

          {/* An open card takes the full width: two expanded columns on a 375px screen
              would each be about 170px, which is four words a line. */}
          <div className="grid grid-cols-2 items-start gap-2">
            {feeders.map((c) => (
              <ColumnCard key={c.key} column={c} open={!!open[c.key]}
                          onToggle={() => toggle(c.key)}
                          className={open[c.key] ? 'col-span-2' : ''}>
                {c.blocks.map((b) => <Block key={b.title} tone={c.tone} block={b} />)}
                <GovernedBy tone={c.tone} items={c.governedBy} />
              </ColumnCard>
            ))}
          </div>
        </div>
      )}

      {/* Side by side, and the plain stack for a slide with no hub (the operating model). */}
      <div className={`space-y-1 lg:space-y-0 ${hub ? 'hidden ' : ''}${
        hasConnectors ? DECK_GRID : DECK_GRID_PLAIN}`}>
        {slide.columns.map((c, i) => (
          <React.Fragment key={c.key}>
            {i > 0 && (
              <FlowConnector
                flows={slide.flows || []}
                fromKey={slide.columns[i - 1].key}
                toKey={c.key}
              />
            )}
            <ColumnCard column={c} open={!!open[c.key]} onToggle={() => toggle(c.key)}>
              {c.blocks.map((b) => <Block key={b.title} tone={c.tone} block={b} />)}
              <GovernedBy tone={c.tone} items={c.governedBy} />
            </ColumnCard>
          </React.Fragment>
        ))}
      </div>

      {slide.sequence && (
        <StepRail
          label={slide.sequence.label}
          steps={slide.sequence.steps}
          note={slide.sequence.note}
        />
      )}

      <Footnote>{slide.footnote}</Footnote>
    </div>
  );
}
